import Page from "~/pages/Page";
import { getApi, handleError, logout } from "~/auth";
import { gotoLogin, navigateTo } from "~/utils";
import UI from "~/ui";
import $ from "jquery";

/**
 * Interfaccia per i dati della ricerca salvata (JSON)
 */
interface RicercaSalvataData {
    ListCategoriaId?: number[];
    Comune?: string;
    Provincia?: string;
    PrezzoMin?: number;
    PrezzoMax?: number;
    SuperficieMin?: number;
    SuperficieMax?: number;
    ListSubCategoriaId?: number[];
}

/**
 * Interfaccia per i dati estratti dalla ricerca
 */
interface ExtractedSearchData {
    Categoria: number;
    Comune: string;
    Provincia: string;
    PrezzoMin: string;
    PrezzoMax: string;
    SuperficieMin: string;
    SuperficieMax: string;
    Subcateg: string;
}

/**
 * Classe per la gestione delle ricerche salvate dell'utente
 */
class RicercheSalvate extends Page {

    private auth: any = null;
    private userInfo: any = null;
    private UserId: number = 0;

    constructor() {
        super("ricerchesalvate", "Ricerche Salvate");
    }

    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");

        if (!this.userInfo) {
            gotoLogin();
            return Promise.reject("User not authenticated");
        }

        this.UserId = this.userInfo.anagraficaId;

        // Inizializza gli event handler
        this.initEventHandlers();

        return Promise.resolve();
    }

    /**
     * Inizializza tutti gli event handler della pagina
     */
    private initEventHandlers(): void {
        this.initDeleteButtons();
        this.initSearchButtons();
    }

    /**
     * Inizializza i pulsanti di eliminazione ricerca
     */
    private initDeleteButtons(): void {
        $(".elimina").on("click", (e) => {
            e.preventDefault();
            const element = e.currentTarget as HTMLElement;
            const idRicerca = $(element).data("id") as number;

            if (!idRicerca || isNaN(idRicerca)) {
                console.error("ID ricerca non valido:", idRicerca);
                UI.confirmOneButton("", "Errore: ID ricerca non valido");
                return;
            }

            UI.confirm(
                "Conferma eliminazione", 
                "Sei sicuro di voler eliminare questa ricerca salvata?", 
                "", 
                async (result: boolean) => {
                    if (result) {
                        await this.removeUserSearch(idRicerca);
                    }
                }
            );
        });
    }

    /**
     * Inizializza i pulsanti di avvio ricerca
     */
    private initSearchButtons(): void {
        $(".btnAvviaRicerca").on("click", (e) => {
            e.preventDefault();

            try {
                const element = e.currentTarget as HTMLElement;
                const idRicerca = $(element).data("id") as number;

                if (!idRicerca || isNaN(idRicerca)) {
                    console.error("ID ricerca non valido:", idRicerca);
                    UI.confirmOneButton("", "Errore: ID ricerca non valido");
                    return;
                }

                this.avviaRicerca(idRicerca);

            } catch (error) {
                console.error("Errore durante l'avvio della ricerca:", error);
                UI.confirmOneButton("", "Si è verificato un errore durante l'avvio della ricerca");
            }
        });
    }

    /**
     * Avvia una ricerca salvata
     * @param idRicerca - ID della ricerca da avviare
     */
    private avviaRicerca(idRicerca: number): void {
        try {
            // Recupera i dati JSON della ricerca
            const ricercaInput = $(`#card-${idRicerca}`).find('input[name="jsondata"]');
            const jsonString = ricercaInput.val() as string;

            if (!jsonString || jsonString.trim() === '') {
                console.error("Dati ricerca non trovati per ID:", idRicerca);
                UI.confirmOneButton("", "Dati della ricerca non trovati");
                return;
            }

            // Parse e validazione JSON
            let jsonData: RicercaSalvataData;
            try {
                jsonData = JSON.parse(jsonString);
            } catch (parseError) {
                console.error("Errore nel parsing JSON:", parseError);
                UI.confirmOneButton("", "Errore nel caricamento dei dati della ricerca");
                return;
            }

            // Estrae e salva i dati
            const extractedData = this.extractSearchData(jsonData);
            this.saveToSessionStorage(extractedData);

            // Submit del form
            const form = $(`#frm${idRicerca}`);
            if (form.length) {
                form.submit();
            } else {
                console.error("Form non trovato per ID:", idRicerca);
                UI.confirmOneButton("", "Errore durante l'avvio della ricerca");
            }

        } catch (error) {
            console.error("Errore in avviaRicerca:", error);
            UI.confirmOneButton("", "Si è verificato un errore imprevisto");
        }
    }

    /**
     * Estrae i dati dalla ricerca salvata
     * @param jsonData - Dati JSON della ricerca
     * @returns Dati estratti formattati
     */
    private extractSearchData(jsonData: RicercaSalvataData): ExtractedSearchData {
        return {
            Categoria: Array.isArray(jsonData.ListCategoriaId) && jsonData.ListCategoriaId.length > 0
                ? jsonData.ListCategoriaId[0]
                : 2,
            Comune: jsonData.Comune || "",
            Provincia: jsonData.Provincia || "",
            PrezzoMin: jsonData.PrezzoMin?.toString() || "",
            PrezzoMax: jsonData.PrezzoMax?.toString() || "",
            SuperficieMin: jsonData.SuperficieMin?.toString() || "",
            SuperficieMax: jsonData.SuperficieMax?.toString() || "",
            Subcateg: Array.isArray(jsonData.ListSubCategoriaId) && jsonData.ListSubCategoriaId.length > 0
                ? jsonData.ListSubCategoriaId[0].toString()
                : ""
        };
    }

    /**
     * Salva i dati estratti nel sessionStorage
     * @param data - Dati da salvare
     */
    private saveToSessionStorage(data: ExtractedSearchData): void {
        try {
            sessionStorage.setItem("categ", data.Categoria.toString());
            sessionStorage.setItem("luogo", data.Comune);
            sessionStorage.setItem("prezzoMin", data.PrezzoMin);
            sessionStorage.setItem("prezzoMax", data.PrezzoMax);
            sessionStorage.setItem("superficieMin", data.SuperficieMin);
            sessionStorage.setItem("superficieMax", data.SuperficieMax);
            sessionStorage.setItem("subcateg", data.Subcateg);
        } catch (error) {
            console.error("Errore nel salvataggio in sessionStorage:", error);
            throw error;
        }
    }

    /**
     * Rimuove una ricerca salvata
     * @param idRicerca - ID della ricerca da eliminare
     */
    private async removeUserSearch(idRicerca: number): Promise<void> {
        const spinner = this.spinner.spin();

        try {
            const url = `${window.BaseUrlWebApi}/RicercaSalvata/Delete?id=${idRicerca}`;

            await this.auth.fetchWithAuth(`DELETE ${url}`, {
                cache: "no-cache",
                headers: { 'content-type': 'application/json' },
            });

            // Rimuove l'elemento dal DOM con animazione
            $(`#box${idRicerca}`).fadeOut(300, () => {
                $(`#box${idRicerca}`).remove();

                // Controlla se esistono altre ricerche salvate
                this.checkRemainingSearches();
            });

            //UI.confirmOneButton("", "Ricerca eliminata con successo");

        } catch (error) {
            console.error("Errore durante la cancellazione della ricerca salvata:", error);
            UI.confirmOneButton("", "Errore durante la cancellazione della ricerca salvata");
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * Controlla se esistono ricerche salvate rimanenti
     * Se non ci sono più ricerche, reindirizza alla pagina "nessuna ricerca salvata"
     */
    private checkRemainingSearches(): void {
        // Conta le ricerche rimanenti (box visibili)
        const remainingSearches = $('[id^="box"]').filter(':visible').length;

        if (remainingSearches === 0) {
            console.log("Nessuna ricerca salvata rimanente, reindirizzamento...");

            // Reindirizza alla pagina "nessuna ricerca salvata"
            setTimeout(() => {
                navigateTo("/nessuna-ricerca-salvata");
            }, 500); // Piccolo delay per permettere all'utente di vedere il messaggio
        }
    }
}

export default RicercheSalvate;